import { gql } from '@apollo/client';
import { useMemo } from 'react';
import { string } from './prop-types';

const walk = (object, ssr) => {
  if (object._client && ssr) {
    return null;
  }
  const objs = Object.keys(object).map((key) => {
    if (key === '_query') {
      return object[key];
    }
    if (key === '_skip') {
      const obj = object[key];
      return {
        [obj._value]: {
          type: 'Boolean',
          _skip: true,
          _value: `Boolean${typeof obj._defaultValue === 'undefined' ? '!' : ''}`,
          _defaultValue: object[key]._defaultValue
        }
      };
    }

    if (typeof object[key] === 'object') {
      return walk(object[key], ssr);
    }
    return null;
  })
    .filter((val) => val)
    .flat();

  if (!objs.length) return {};

  return objs.reduce((acc, cur) => {
    return {
      ...acc,
      ...cur,
    };
  }, {});
};

export const getQueryArgs = ({ attributes = {}, asString = true, ssr = true }) => {
  const args = walk(attributes, ssr);

  if (!asString) return args;

  return Object.keys(args).map((key) => {
    const defaultValue = typeof args[key]._defaultValue !== 'undefined'
      ? `=${args[key]._defaultValue}`
      : '';
    if (args[key]._isArray) {
      const isRequired = args[key]._isRequired ? '!' : '';
      return `$${key}: [${args[key]._children._value}]${isRequired}`;
    }

    return `$${key}:${typeof args[key] === 'object' ? args[key]._value : args[key]}${defaultValue}`;
  }).join(', ');

};

export const getQueryParams = ({ attributes = {} }) => {

  return Object.keys(attributes).map((key) => {
    return `${key}:$${key}`;
  }).join(', ');

};

export const getQueryType = ({ attributes, name }) => {
  if (attributes[name]?._mutation) return 'mutation';
  return 'query';
};

export const getSubQuery = ({
  attributes = {}, level = 1, ssr, clientResolver = false, skipDirective
}) => {
  const spacing = new Array(level).fill('  ').join('');

  return Object.keys(attributes)
    .map((domain) => {

      if (attributes[domain]._client && ssr) return '';

      // eslint-disable-next-line max-len
      if (attributes[domain].type === 'Object' && attributes[domain]._isArray && attributes[domain]?._children?.type !== 'String') {
        return getSubQuery({
          attributes: { [domain]: attributes[domain]._children },
          ssr,
        });
      }

      let queryParam = domain;
      if (attributes[domain]._query) {
        const params = getQueryParams({ attributes: attributes[domain]._query });
        if (params) {
          queryParam = `${domain}(${params})`;
        }
      }

      const skipDirectiveString = skipDirective
        ? `@skip(if:$${skipDirective._value})`
        : '';

      // array response with just a string
      if (attributes[domain]?._children?.type === 'String' && attributes[domain]._isArray) {
        return `${spacing} ${queryParam} ${skipDirectiveString} ${clientResolver ? '@client' : ''}`;
      }

      // just a string response
      if (attributes[domain]?.type === 'String') {
        return `${spacing} ${queryParam} ${skipDirectiveString} ${clientResolver ? '@client' : ''}`;
      }
      return `
${spacing} ${queryParam} ${skipDirectiveString} ${clientResolver ? '@client' : ''} {
  ${Object.keys(attributes[domain])
    .map((attribute) => {

      if (/^_/.test(attribute)) {
        return '';
      }

      const suffix = attributes[domain][attribute]._clientResolver
        ? ' @client'
        : '';

      if (!attributes[domain][attribute]._isPropType) {
        if (attributes[domain][attribute].type === 'Object') {
          // primiative as a propType should not render children in query
          if (attributes[domain][attribute]._children._isPropType) {
            return spacing + attribute + suffix;
          }

          // arrays that are client only should return empty
          if (attributes[domain][attribute]._client && ssr) return '';

          return getSubQuery({
            clientResolver: attributes[domain][attribute]._clientResolver,
            skipDirective: attributes[domain][attribute]._skip,
            attributes: { [attribute]: attributes[domain][attribute]._children },
            ssr,
            level: level + 1
          });
        }
        return getSubQuery({
          attributes: { [attribute]: attributes[domain][attribute] },
          ssr,
        });
      }

      return spacing + attribute + suffix;
    })
    .join('\n' + spacing)}
${spacing}}
`;
    })
    .join('');

};

export const getQueryFromAttributes = ({ name, attributes = {}, level = 1, ssr = true }) => {
  // if (attributes.product?._query) {
  //   // eslint-disable-next-line no-param-reassign
  //   attributes.product._query.dataSource = string();
  // }
  const query = getSubQuery(({ attributes, level, ssr }));
  const args = getQueryArgs({ attributes, ssr });
  const queryType = getQueryType({ attributes, name });

  const argsWrapper = args
    ? `(${args})`
    : '';

  return gql(`
${queryType} ${name}${argsWrapper} {
  ${query}
}
  `);
};
